import { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Card, Row, Col, Tag, Button, Typography, Spin, Tooltip, Progress } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  MinusCircleOutlined,
  LoadingOutlined,
  ApiOutlined,
  SafetyOutlined,
  DatabaseOutlined,
  CloudSyncOutlined,
  DesktopOutlined,
  MedicineBoxOutlined,
} from "@ant-design/icons";
import { buildChecks, STATUS } from "./healthChecks";

const { Title, Text } = Typography;

const CATEGORY_CONFIG = {
  Backend: { icon: <ApiOutlined />, color: "#1890ff", order: 1 },
  "Till & Session": { icon: <SafetyOutlined />, color: "#722ed1", order: 2 },
  "Sync Data": { icon: <CloudSyncOutlined />, color: "#13c2c2", order: 3 },
  Hardware: { icon: <DesktopOutlined />, color: "#fa8c16", order: 4 },
  Storage: { icon: <DatabaseOutlined />, color: "#52c41a", order: 5 },
};

const STATUS_CONFIG = {
  [STATUS.PASS]: { color: "#52c41a", icon: <CheckCircleOutlined />, label: "Pass" },
  [STATUS.FAIL]: { color: "#ff4d4f", icon: <CloseCircleOutlined />, label: "Fail" },
  [STATUS.WARN]: { color: "#faad14", icon: <ExclamationCircleOutlined />, label: "Warning" },
  [STATUS.RUNNING]: { color: "#1890ff", icon: <LoadingOutlined spin />, label: "Running" },
  [STATUS.SKIP]: { color: "#d9d9d9", icon: <MinusCircleOutlined />, label: "Skipped" },
};

const SEVERITY_COLOR = { critical: "#ff4d4f", high: "#fa8c16", medium: "#1890ff", low: "#d9d9d9" };

function HealthMonitor() {
  const history = useHistory();
  const [checks, setChecks] = useState(() => buildChecks());
  const [running, setRunning] = useState(false);
  const abortRef = useRef(null);

  const runAllChecks = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setRunning(true);
    const freshChecks = buildChecks();
    setChecks(freshChecks);

    for (let i = 0; i < freshChecks.length; i++) {
      if (controller.signal.aborted) return;
      const check = freshChecks[i];
      try {
        const result = await check.run();
        if (controller.signal.aborted) return;
        setChecks((prev) => {
          const next = [...prev];
          next[i] = { ...next[i], ...result };
          return next;
        });
      } catch (err) {
        if (controller.signal.aborted) return;
        setChecks((prev) => {
          const next = [...prev];
          next[i] = { ...next[i], status: STATUS.FAIL, detail: `Unexpected: ${err.message}` };
          return next;
        });
      }
    }
    setRunning(false);
  }, []);

  const runSingleCheck = useCallback(async (index) => {
    setChecks((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], status: STATUS.RUNNING, detail: "" };
      return next;
    });
    const check = buildChecks()[index];
    try {
      const result = await check.run();
      setChecks((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], ...result };
        return next;
      });
    } catch (err) {
      setChecks((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], status: STATUS.FAIL, detail: `Unexpected: ${err.message}` };
        return next;
      });
    }
  }, []);

  useEffect(() => {
    runAllChecks();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [runAllChecks]);

  const counts = checks.reduce(
    (acc, c) => {
      if (c.status === STATUS.PASS) acc.pass++;
      else if (c.status === STATUS.FAIL) acc.fail++;
      else if (c.status === STATUS.WARN) acc.warn++;
      else if (c.status === STATUS.RUNNING) acc.running++;
      return acc;
    },
    { pass: 0, fail: 0, warn: 0, running: 0 }
  );

  const totalDone = counts.pass + counts.fail + counts.warn;
  const total = checks.filter((c) => c.status !== STATUS.SKIP).length;
  const pct = total > 0 ? Math.round((counts.pass / total) * 100) : 0;
  const overallColor = counts.fail > 0 ? "#ff4d4f" : counts.warn > 0 ? "#faad14" : "#52c41a";

  const grouped = {};
  for (const check of checks) {
    if (!grouped[check.category]) grouped[check.category] = [];
    grouped[check.category].push(check);
  }
  const sortedCategories = Object.keys(grouped).sort(
    (a, b) => (CATEGORY_CONFIG[a]?.order || 99) - (CATEGORY_CONFIG[b]?.order || 99)
  );

  return (
    <div style={{ background: "#f0f2f5", minHeight: "100vh", padding: "24px" }}>
      {/* Header */}
      <Row align="middle" justify="space-between" style={{ marginBottom: 24 }}>
        <Col>
          <Row align="middle" gutter={16}>
            <Col>
              <Button
                icon={<ArrowLeftOutlined />}
                type="text"
                onClick={() => history.goBack()}
                style={{ fontSize: 18 }}
              />
            </Col>
            <Col>
              <Title level={3} style={{ margin: 0 }}>
                <MedicineBoxOutlined style={{ marginRight: 8 }} />
                Health Monitor
              </Title>
              <Text type="secondary">System readiness diagnostic</Text>
            </Col>
          </Row>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<ReloadOutlined spin={running} />}
            onClick={runAllChecks}
            loading={running}
            size="large"
          >
            Re-check All
          </Button>
        </Col>
      </Row>

      {/* Summary Bar */}
      <Card style={{ marginBottom: 24, borderLeft: `4px solid ${overallColor}` }}>
        <Row align="middle" gutter={24}>
          <Col flex="120px">
            <Progress
              type="circle"
              percent={pct}
              size={80}
              strokeColor={overallColor}
              format={() => (
                <span style={{ fontSize: 20, fontWeight: 600, color: overallColor }}>
                  {counts.running > 0 ? <LoadingOutlined spin /> : `${pct}%`}
                </span>
              )}
            />
          </Col>
          <Col flex="auto">
            <Row gutter={24}>
              <Col>
                <Text strong style={{ fontSize: 16 }}>
                  {totalDone}/{total} checks complete
                </Text>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col>
                <Tag color="success" icon={<CheckCircleOutlined />}>
                  {counts.pass} Passed
                </Tag>
              </Col>
              <Col>
                <Tag color="error" icon={<CloseCircleOutlined />}>
                  {counts.fail} Failed
                </Tag>
              </Col>
              <Col>
                <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                  {counts.warn} Warnings
                </Tag>
              </Col>
              {counts.running > 0 && (
                <Col>
                  <Tag color="processing" icon={<LoadingOutlined spin />}>
                    {counts.running} Running
                  </Tag>
                </Col>
              )}
            </Row>
          </Col>
          <Col>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {new Date().toLocaleTimeString()}
            </Text>
          </Col>
        </Row>
      </Card>

      {/* Check Categories */}
      {sortedCategories.map((category) => {
        const catConfig = CATEGORY_CONFIG[category] || { icon: <DatabaseOutlined />, color: "#999", order: 99 };
        const catChecks = grouped[category];
        const catFailed = catChecks.some((c) => c.status === STATUS.FAIL);
        const catWarned = catChecks.some((c) => c.status === STATUS.WARN);

        return (
          <Card
            key={category}
            title={
              <Row align="middle" gutter={8}>
                <Col style={{ color: catConfig.color, fontSize: 18 }}>{catConfig.icon}</Col>
                <Col>
                  <Text strong style={{ fontSize: 16 }}>
                    {category}
                  </Text>
                </Col>
                <Col>
                  {catFailed ? (
                    <Tag color="error" style={{ fontSize: 11 }}>Issues Found</Tag>
                  ) : catWarned ? (
                    <Tag color="warning" style={{ fontSize: 11 }}>Warnings</Tag>
                  ) : (
                    <Tag color="success" style={{ fontSize: 11 }}>All Good</Tag>
                  )}
                </Col>
              </Row>
            }
            style={{ marginBottom: 16 }}
            styles={{ body: { padding: "8px 16px" } }}
          >
            {catChecks.map((check, idx) => {
              const globalIdx = checks.findIndex((c) => c.id === check.id);
              const sc = STATUS_CONFIG[check.status] || STATUS_CONFIG[STATUS.RUNNING];
              return (
                <Row
                  key={check.id}
                  align="middle"
                  style={{
                    padding: "12px 8px",
                    borderBottom: idx < catChecks.length - 1 ? "1px solid #f0f0f0" : "none",
                    background: check.status === STATUS.FAIL ? "#fff2f0" : check.status === STATUS.WARN ? "#fffbe6" : "transparent",
                    borderRadius: 4,
                  }}
                >
                  <Col flex="32px" style={{ color: sc.color, fontSize: 20 }}>
                    {sc.icon}
                  </Col>
                  <Col flex="auto">
                    <Row align="middle" gutter={8}>
                      <Col>
                        <Text strong>{check.name}</Text>
                      </Col>
                      <Col>
                        <Tooltip title={`Severity: ${check.severity}`}>
                          <span
                            style={{
                              display: "inline-block",
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: SEVERITY_COLOR[check.severity] || "#999",
                            }}
                          />
                        </Tooltip>
                      </Col>
                    </Row>
                    {check.detail && (
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        {check.detail}
                      </Text>
                    )}
                  </Col>
                  <Col>
                    <Row gutter={8} align="middle">
                      {check.action && check.status !== STATUS.RUNNING && (
                        <Col>
                          <Tag color="blue" style={{ fontSize: 11, cursor: "default" }}>
                            {check.action}
                          </Tag>
                        </Col>
                      )}
                      <Col>
                        <Tooltip title="Re-run this check">
                          <Button
                            type="text"
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={() => runSingleCheck(globalIdx)}
                            disabled={running || check.status === STATUS.RUNNING}
                          />
                        </Tooltip>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              );
            })}
          </Card>
        );
      })}

      {/* Footer */}
      <div style={{ textAlign: "center", padding: "16px 0" }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Health Monitor v1.0 — Checks run automatically on page load
        </Text>
      </div>
    </div>
  );
}

export default HealthMonitor;
